import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CalendarClock, AlertTriangle, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '@/components/ui/dialog';
import {
  disconnectGoogleCalendar,
  getGoogleCalendarConnectUrl,
  getGoogleCalendarStatus,
  GoogleCalendarStatus,
} from '../../../hooks/api/googleCalendar';
import { toastUtils } from '../../../hooks/useToast';
import { GoogleCalendarHelpModal } from './GoogleCalendarHelpModal';
import { activityTracker } from '../../../utils/activityTracker';

/**
 * Botón de Perfil para conectar/desconectar la sincronización con Google Calendar
 * (habilitado para todo el equipo, detrás de un interruptor maestro del backend). Si el
 * backend responde `enabled: false` (el interruptor está apagado) y no hay una conexión
 * previa, la card no se renderiza — mantiene el Perfil sin ruido mientras está apagado.
 *
 * El flujo de conexión es una navegación completa (`window.location.href`), no un fetch:
 * el backend responde con la URL de consentimiento de Google y el browser la sigue,
 * Google redirige a un callback del backend, y ese callback redirige de vuelta a
 * `/profile?google_calendar=connected|error`, que esta card detecta al montar.
 */
export const GoogleCalendarIntegrationCard: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState<GoogleCalendarStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const data = await getGoogleCalendarStatus();
      setStatus(data);
    } catch {
      setStatus(null);
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // Resultado del callback OAuth (?google_calendar=connected|error), seteado por el
  // backend al redirigir de vuelta acá. Se limpia el query param para no reprocesarlo
  // en un refresh de la página.
  useEffect(() => {
    const result = searchParams.get('google_calendar');
    if (!result) return;
    if (result === 'connected') {
      toastUtils.success('Google Calendar conectado correctamente');
      // Métricas de Actividad (funnel google_calendar): volvió del OAuth conectada. Este efecto corre al
      // montar, antes de que el tracker lea su config: el tracker la guarda y la reproduce.
      activityTracker.trackAction('gcal_connected');
      loadStatus();
    } else if (result === 'error') {
      toastUtils.error('No se pudo conectar Google Calendar. Intentá de nuevo.');
    }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('google_calendar');
      return next;
    }, { replace: true });
    // Solo procesar una vez al montar / cuando cambia el query param de resultado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleConnect = async () => {
    // Métricas de Actividad: empieza a conectar (antes del redirect a Google, que descarga la página).
    activityTracker.trackAction('gcal_connect_started');
    setConnecting(true);
    try {
      const { url } = await getGoogleCalendarConnectUrl();
      window.location.href = url;
    } catch {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await disconnectGoogleCalendar();
      toastUtils.success('Google Calendar desconectado');
      setShowDisconnectConfirm(false);
      await loadStatus();
    } finally {
      setDisconnecting(false);
    }
  };

  if (loadingStatus) {
    return (
      <div
        data-testid="google-calendar-card-loading"
        className="bg-gray-50 rounded-xl px-3 pt-2.5 pb-3 sm:px-4 sm:pt-3 sm:pb-4 min-w-0 mb-4 sm:mb-5"
      >
        <div className="h-3 w-40 skeleton-shimmer rounded mb-2" />
        <div className="h-8 w-48 skeleton-shimmer rounded-lg" />
      </div>
    );
  }

  // Interruptor maestro apagado y sin conexión previa: no mostrar nada.
  if (!status || (!status.enabled && !status.connected)) {
    return null;
  }

  // Sync rota (ej. la psicóloga revocó el acceso desde su cuenta de Google, o el refresh
  // token quedó inválido) — sin esto, el job reintenta en silencio cada 12 min para
  // siempre y la psicóloga nunca se entera de que dejó de sincronizar (`lastSyncError` ya
  // se guardaba en la integración, pero no se mostraba en ningún lado).
  const needsReconnect = status.connected && Boolean(status.lastSyncError);

  return (
    <div
      data-testid="google-calendar-card"
      className="bg-gray-50 rounded-xl px-3 pt-2.5 pb-3 sm:px-4 sm:pt-3 sm:pb-4 min-w-0 flex items-center justify-between gap-2 mb-4 sm:mb-5"
    >
      <div className="min-w-0 flex items-start gap-2">
        {needsReconnect ? (
          <AlertTriangle size={18} className="text-amber-500 mt-0.5 shrink-0" />
        ) : (
          <CalendarClock size={18} className="text-gray-500 mt-0.5 shrink-0" />
        )}
        <div className="min-w-0">
          <p className="text-[10px] sm:text-[11px] font-medium uppercase tracking-wider mb-1 sm:mb-1.5 text-gray-400 flex items-center gap-1">
            Google Calendar
            <button
              type="button"
              aria-label="Cómo funciona la sincronización con Google Calendar"
              data-testid="google-calendar-help-btn"
              onClick={() => setShowHelpModal(true)}
              className="inline-flex items-center justify-center w-[18px] h-[18px] rounded-full text-gray-400 hover:text-gray-600 hover:bg-black/5 transition-colors"
            >
              <Info size={12} />
            </button>
          </p>
          {needsReconnect ? (
            <>
              <p className="text-xs sm:text-sm font-medium text-amber-700 break-words" data-testid="google-calendar-needs-reconnect-label">
                Necesita reconectar
              </p>
              <p className="text-[11px] sm:text-xs text-amber-600 break-words mt-0.5" data-testid="google-calendar-sync-error-message">
                {status.lastSyncError}
              </p>
            </>
          ) : status.connected ? (
            <p className="text-xs sm:text-sm font-normal text-gray-800 break-words" data-testid="google-calendar-connected-label">
              Conectado{status.googleEmail ? ` como ${status.googleEmail}` : ''}
            </p>
          ) : (
            <p className="text-xs sm:text-sm text-gray-500">
              Sincronizá tus sesiones con tu Google Calendar
            </p>
          )}
        </div>
      </div>
      <div className="flex-shrink-0 flex flex-col items-end gap-1">
        {needsReconnect ? (
          <>
            <Button
              type="button"
              variant="primary"
              size="sm"
              data-testid="google-calendar-reconnect-btn"
              onClick={handleConnect}
              disabled={connecting}
            >
              {connecting ? 'Conectando…' : 'Reconectar'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-auto p-0 text-[11px] font-normal underline hover:bg-transparent"
              data-testid="google-calendar-disconnect-btn"
              onClick={() => setShowDisconnectConfirm(true)}
            >
              Desconectar
            </Button>
          </>
        ) : status.connected ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="google-calendar-disconnect-btn"
            onClick={() => setShowDisconnectConfirm(true)}
          >
            Desconectar
          </Button>
        ) : (
          <Button
            type="button"
            variant="primary"
            size="sm"
            data-testid="google-calendar-connect-btn"
            onClick={handleConnect}
            disabled={connecting}
          >
            {connecting ? 'Conectando…' : 'Conectar Google Calendar'}
          </Button>
        )}
      </div>

      <Dialog open={showDisconnectConfirm} onOpenChange={setShowDisconnectConfirm}>
        <DialogContent className="p-6 sm:max-w-md w-full">
          <DialogHeader>
            <DialogTitle>¿Desconectar Google Calendar?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 mb-4">
            Los eventos que ya se crearon van a seguir estando en tu Google Calendar — no se
            borran. Pero a partir de ahora, si agendás, reprogramás o cancelás una sesión en
            Psi Platform, ese cambio no se va a reflejar ahí hasta que vuelvas a conectar.
          </p>
          <div className="flex justify-end gap-2">
            <DialogClose asChild>
              <Button type="button" variant="outline" size="sm">
                Cancelar
              </Button>
            </DialogClose>
            <Button
              type="button"
              variant="default"
              size="sm"
              data-testid="google-calendar-disconnect-confirm-btn"
              onClick={handleDisconnect}
              disabled={disconnecting}
            >
              {disconnecting ? 'Desconectando…' : 'Sí, desconectar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <GoogleCalendarHelpModal
        open={showHelpModal}
        onClose={() => setShowHelpModal(false)}
      />
    </div>
  );
};

export default GoogleCalendarIntegrationCard;
